import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { Store } from '@ngxs/store';
import { PatchORDBitDto, PatchORDCutterMapDto, PatchORDGaugeProtectionDto } from '../../interfaces/request.interface';
import { takeUntil, debounceTime, Subject, Observable, BehaviorSubject, combineLatest, map, filter, startWith, take, skip, finalize, tap } from 'rxjs';
import { RequestActions } from '../../store/action/request.action';
import { OptionState } from '../../store/state/options.state';
import { AccountState } from '../../store/state/account.state';
import { FormBuilder, FormControl, FormGroup, Validators, ValidatorFn, AbstractControl } from '@angular/forms';
import { groupBy, GroupResult } from '@progress/kendo-data-query';
import { RowClassArgs } from '@progress/kendo-angular-grid';
import { AvailableCutter, Chamfer, CutterGroup } from '../../interfaces/cutterMap';
import { SVGIcon, saveIcon, plusIcon, minusIcon, pencilIcon, copyIcon, moreHorizontalIcon, trashIcon } from "@progress/kendo-svg-icons";
import { RTAApiClient } from '../../services/rta-api.service';
import { CreateCutterOverrideRequestDto } from '../../interfaces/request.interface';
import { PermissionViewModel } from '../../interfaces/viewmodels/userviewmodel';

interface FamilyTypeOption {
  Name: string;
  group: string;
  display: string;
}

@Component({
    selector: 'app-cuttermapping-data',
    templateUrl: './cuttermapping-data.component.html',
    styleUrl: './cuttermapping-data.component.css',
    standalone: false
})
export class CuttermappingDataComponent {
  private _initialMappings: PatchORDCutterMapDto[] = [];
  public svgSave: SVGIcon = saveIcon;
  public get cutterMappingInitial(): PatchORDCutterMapDto[] {
    return this._initialMappings;
  }

  public colorCodes = [{ Id: 1, Color: '#3D37FF' }, { Id: 2, Color: '#FF3737' }, { Id: 3, Color: '#00FF37' }, { Id: 4, Color: '#FF37F3' }, { Id: 5, Color: '#37FFD1' }, { Id: 6, Color: '#FFA137' },
  { Id: 7, Color: '#8E37FF' }, { Id: 8, Color: '#96FF9F' }, { Id: 9, Color: '#FF37A1' }, { Id: 10, Color: '#37A1FF' }, { Id: 11, Color: '#FF37FF' }, { Id: 12, Color: '#37FF8E' },
  { Id: 13, Color: '#FF8E37' }, { Id: 14, Color: '#37FF00' }, { Id: 15, Color: '#FF00FF' }, { Id: 16, Color: '#00FF8E' }, { Id: 17, Color: '#FF3737' }, { Id: 18, Color: '#37FFA1' },
  { Id: 19, Color: '#FF8E8E' }, { Id: 20, Color: '#8E8EFF' }, { Id: 21, Color: '#FF00A1' }, { Id: 22, Color: '#00A1FF' }, { Id: 23, Color: '#A1FF00' }, { Id: 24, Color: '#FFA1FF' },
  { Id: 25, Color: '#A1FF8E' }]

  public zones = [{ name: 'CONE' }, { name: 'NOSE' }, { name: 'SHOULDER' }, { name: 'GAUGE' }]
  public locations = [{ name: 'Primary' }, { name: 'Secondary' }]
  @Input() isEJDF: boolean = false;
  @Input() readonly: boolean = false;
  @Input() set cutterMappingInitial(v: PatchORDCutterMapDto[] | undefined) {
    this._initialMappings = v ?? [];
    this.mapping$.next(this._initialMappings);
    this.rebuildBlades();
  }
  @Output() cutterQuantities = new EventEmitter<{ cutter: AvailableCutter; qty: number, index: number }[]>();
  private mapping$ = new BehaviorSubject<PatchORDCutterMapDto[]>([]);
  public arms: number[] = [];
  public hasArmNumbers = false;

  /**
   * Sorts blades within a single arm.
   *
   * Rule:
   *   - If this arm contains the overall minimum blade of the entire map
   *     (i.e. it is "Arm 1"), that minimum blade is placed first and all
   *     remaining blades follow in descending order.
   *   - All other arms are sorted fully descending (highest blade first).
   *
   * Examples:
   *   1 blade/arm:  A1[1]→[1]   A2[3]→[3]   A3[2]→[2]
   *   2 blades/arm: A1[1,6]→[1,6]  A2[5,4]→[5,4]  A3[3,2]→[3,2]
   *   3 blades/arm: A1[1,9,8]→[1,9,8]  A2[4,3,2]→[4,3,2]  A3[7,6,5]→[7,6,5]
   */
  private sortBladesWithinArm(blades: number[], isFirstArm: boolean): number[] {
    if (blades.length <= 1) return [...blades];
    const sorted = [...blades].sort((a, b) => a - b);
    if (isFirstArm) {
      // Anchor (lowest) first, then remaining descending
      const anchor = sorted[0];
      const rest = sorted.slice(1).sort((a, b) => b - a);
      return [anchor, ...rest];
    }
    // All other arms: fully descending
    return sorted.reverse();
  }

  private buildArmLookups(mappings: PatchORDCutterMapDto[]): {
    armForBlade: Map<number, number>;
    minBladeForArm: Map<number, number>;
    armsOrdered: number[];
  } {
    const minBladeForArm = new Map<number, number>();
    const armForBlade = new Map<number, number>();

    // Walk blades ascending so the first encounter per arm is the minimum (anchor)
    const sorted = [...mappings].sort((a, b) => (a.bladeNumber ?? 0) - (b.bladeNumber ?? 0));
    sorted.forEach(m => {
      if (m.bladeNumber == null || m.armNumber == null) return;
      armForBlade.set(m.bladeNumber, m.armNumber);
      if (!minBladeForArm.has(m.armNumber)) {
        minBladeForArm.set(m.armNumber, m.bladeNumber);
      }
    });

    // Arms ordered by their anchor (lowest) blade number, ties broken by arm number
    const armsOrdered = Array.from(minBladeForArm.keys()).sort((a, b) => {
      const minA = minBladeForArm.get(a) ?? 0;
      const minB = minBladeForArm.get(b) ?? 0;
      return minA !== minB ? minA - minB : a - b;
    });

    return { armForBlade, minBladeForArm, armsOrdered };
  }

  private rebuildBlades() {
    const mappings = this._initialMappings;
    const onlyNums = mappings.map(m => m.bladeNumber).filter((n): n is number => n != null);
    this.hasArmNumbers = mappings.some(m => m.armNumber != null);

    if (this.hasArmNumbers) {
      const { armForBlade, armsOrdered } = this.buildArmLookups(mappings);
      this.arms = armsOrdered;

      // Group unique blades by arm, sort each arm's blades with anchor-first/rest-descending,
      // then concatenate in arm order
      const bladesByArm = new Map<number, number[]>();
      armsOrdered.forEach(arm => bladesByArm.set(arm, []));
      Array.from(new Set(onlyNums)).forEach(blade => {
        const arm = armForBlade.get(blade);
        if (arm != null) bladesByArm.get(arm)?.push(blade);
      });

      this.blades = armsOrdered.flatMap((arm, armIndex) =>
        this.sortBladesWithinArm(bladesByArm.get(arm) ?? [], armIndex === 0)
      );
    } else {
      this.arms = [];
      this.blades = Array.from(new Set(onlyNums)).sort((a, b) => a - b);
    }
  }

  public getBladesForArm(arm: number): number[] {
    const mappings = this.mapping$.getValue();
    const bladeNums = mappings
      .filter(m => m.armNumber === arm && m.bladeNumber != null)
      .map(m => m.bladeNumber as number);
    const isFirstArm = this.arms[0] === arm;
    return this.sortBladesWithinArm(Array.from(new Set(bladeNums)), isFirstArm);
  }

  public getArmForBlade(blade: number): number | null {
    const m = this.mapping$.getValue().find(x => x.bladeNumber === blade && x.armNumber != null);
    return m?.armNumber ?? null;
  }
  public cutterQuantities$!: Observable<{ cutter: AvailableCutter; qty: number, index: number }[]>;
  public cutterQuantitiesList: Array<{ cutter: AvailableCutter; qty: number; index: number }> = [];


  @Input() draftId?: number;
  @Input() cutterData!: any;
  @Input() set retrofit(value: boolean) {
    this.retrofit$$.next(value);
  }

  private retrofit$$ = new BehaviorSubject<boolean>(false);
  public retrofit$ = this.retrofit$$.asObservable();

  public appcutterMappingData!: FormGroup;
  public mappingTools!: FormGroup;

  // form‐model for primary/backup/global
  public PrimaryCutter: any = {};
  public BackupCutter: any = {};
  public Global: any = {};
  public GlobalBackup: any = {};
  private destroy$ = new Subject<void>();
  public blades: number[] = [];
  public rtaAvailableCuttersStatuses$!: Observable<AvailableCutter[]>;
  public rtaAvailableCutters$!: Observable<AvailableCutter[]>;
  public filteredPrimaryTypes$!: Observable<GroupResult[]>;
  public filteredSecondaryTypes$!: Observable<GroupResult[]>;
  public filteredPlaceCutterTypes$!: Observable<GroupResult[]>;
  public filteredElementTypes$!: Observable<GroupResult[]>;
  public filteredZoneTypes$!: Observable<GroupResult[]>;
  public chamferStatuses$: Observable<Chamfer[]>;
  public cutterChamfers$: Observable<string[]>;
  public cutterGroups$!: Observable<CutterGroup[]>;
  private cutterGroupsList: CutterGroup[] = [];
  public overrideFamilies$!: Observable<{ id: number; name: string }[]>;
  public obsAssignedFamilies$!: Observable<string[]>;
  public selectedElements: number[] = [];
  public highlightedElements: number[] = [];
  public allowSelect: boolean = false;
  public svgPlus: SVGIcon = plusIcon;
  public svgMultiAdd: SVGIcon = moreHorizontalIcon;
  public svgCopy: SVGIcon = copyIcon;
  public svgTrash: SVGIcon = trashIcon;
  public icons = { svgMultiAdd: moreHorizontalIcon, svgPlus: plusIcon, svgMinus: minusIcon, svgEdit: pencilIcon, svgCopy: copyIcon };
  public multiCutterAdd: number = 1;
  
  // Track which blade/row the popover was opened for
  public activeBladeNo: number = 0;
  public activeRowNo: number = 0;

  public elementType: string = '';

  cutterChamfers: string[] = [];
  public overrideForm!: FormGroup;
  public overrideCodeForm!: FormGroup;
  public overrideSubmitting = false;
  public overrideMessage: string | null = null;
  public overrideError: string | null = null;
  public selectionMessage: string | null = null;
  public selectionDetails: string | null = null;
  public primaryMessage: string | null = null;
  public primaryDetails: string | null = null;
  public secondaryMessage: string | null = null;
  public secondaryDetails: string | null = null;
  public elementGroupMessage: string | null = null;
  public elementGroupDetails: string | null = null;
  public zoneMessage: string | null = null;
  public zoneDetails: string | null = null;
  public backupFamilies$!: Observable<FamilyTypeOption[]>;
  private overrideFamiliesList: { id: number; name: string }[] = [];
  private overrideUnlocked$$ = new BehaviorSubject<boolean>(false);
  public overrideUnlocked$ = this.overrideUnlocked$$.asObservable();
  private overrideUnlockedFamilyId?: number;
  public isCutterMapperAdmin = false;
  public user$!: Observable<any>;

  constructor(private store: Store, private fb: FormBuilder, private rtaApi: RTAApiClient) {
    this.user$ = this.store.select(AccountState.user);
    this.appcutterMappingData = this.fb.group({
      id: [null, Validators.required],
      orderId: [null, Validators.required],
      pdcCutterId: [null]
    });
    this.mappingTools = this.fb.group({
      PrimaryCutterChamfer: [null],
      PrimaryCutterType: [{ value: null, disabled: true }],
      SecondaryCutterChamfer: [null],
      SecondaryCutterType: [{ value: null, disabled: true }],
      PlaceCutterChamfer: [null],
      PlaceCutterType: [{ value: null, disabled: true }],
      ElementType: [null],
      ElementChamfer: [null],
      ElementFamilyType: [{ value: null, disabled: true }],
      ZoneName: [null],
      ZoneLocation: [null],
      ZoneChamfer: [null],
      ZoneFamilyType: [{ value: null, disabled: true }],
      BackupFamilyType: [null]
    })
    this.overrideForm = this.fb.group({
      cutterFamily: [null, Validators.required],
      justification: [null, [this.trimmedMinLengthValidator(5)]],
      requesterEmail: [null, [Validators.email]]
    });
    this.overrideCodeForm = this.fb.group({
      overrideCode: [null, Validators.required]
    });

    // Determine if user has admin permission to bypass cutter phase filtering
    this.user$
      .pipe(take(1))
      .subscribe(user => {
        const perms: PermissionViewModel[] = user?.permissions ?? [];
        this.isCutterMapperAdmin = perms.some((p: PermissionViewModel) => p.name === 'RTA_CUTTERMAPPER_ADMIN');
      });
    this.chamferStatuses$ = this.store.select(OptionState.cutterChamfers);
    this.cutterChamfers$ = combineLatest([
      this.chamferStatuses$,
      this.retrofit$.pipe(startWith(this.retrofit$$.value))
    ]).pipe(
      map(([statuses, retrofit]) =>
        statuses
          .filter(s => (retrofit ? true : !s.isObsolete))
          .map(s => s.cutterChamfer)
      )
    );
    this.rtaAvailableCuttersStatuses$ = this.store.select(OptionState.availableCutters);
    this.rtaAvailableCutters$ = combineLatest([
      this.rtaAvailableCuttersStatuses$,
      this.retrofit$.pipe(startWith(this.retrofit$$.value)),
      this.overrideUnlocked$.pipe(startWith(this.overrideUnlocked$$.value))
    ]).pipe(
      map(([statuses, retrofit, unlocked]) => {
        // Admin or retrofit sees everything
        if (retrofit || this.isCutterMapperAdmin) {
          return statuses;
        }

        // If an override code was validated, allow the approved family even if OBS/D; others still filtered
        if (unlocked && this.overrideUnlockedFamilyId != null) {
          return statuses.filter(s => {
            const phase = (s.cutterPhase_Name || '').toUpperCase();
            const famId = this.getCutterFamilyId(s);
            const isApprovedFamily = famId === this.overrideUnlockedFamilyId;
            return isApprovedFamily || (phase !== 'OBS' && phase !== 'D');
          });
        }

        // Default filtering: hide OBS/D
        return statuses.filter(s => {
          const phase = (s.cutterPhase_Name || '').toUpperCase();
          return phase !== 'OBS' && phase !== 'D';
        });
      })
    );
    this.overrideFamilies$ = this.rtaAvailableCuttersStatuses$.pipe(
      map(cutters => {
        const lookup = new Map<string, { id: number; name: string }>();
        cutters.forEach(c => {
          const name = c.familyName ?? c.pdcType;
          const id = this.getCutterFamilyId(c);
          if (name && id != null && !lookup.has(name)) {
            lookup.set(name, { id, name });
          }
        });
        return Array.from(lookup.values()).sort((a, b) => a.name.localeCompare(b.name));
      }),
      tap(list => this.overrideFamiliesList = list)
    );
    this.obsAssignedFamilies$ = combineLatest([
      this.mapping$,
      this.rtaAvailableCuttersStatuses$
    ]).pipe(
      map(([maps, cutters]) => {
        const assignedIds = new Set<number>();
        maps.forEach(m => {
          if (m.pdcCutter_Id != null) {
            assignedIds.add(m.pdcCutter_Id);
          }
        });

        const names = new Set<string>();
        cutters.forEach(c => {
          const phase = (c.cutterPhase_Name || '').toUpperCase();
          if ((phase === 'OBS' || phase === 'D') && assignedIds.has(c.pdcCutterID)) {
            const name = c.name ?? c.pdcType ?? '';
            if (name) {
              names.add(name);
            }
          }
        });

        return Array.from(names).sort((a, b) => a.localeCompare(b));
      })
    );
  }
  private createFilteredTypes$(
    chamferCtrl: FormControl
  ): Observable<GroupResult[]> {
    const selectedChamfer$ = chamferCtrl.valueChanges.pipe(
      startWith(chamferCtrl.value)
    );

    return combineLatest([
      this.rtaAvailableCutters$,
      selectedChamfer$
    ]).pipe(
      map(([cutters, chamfer]) => {
        const unlocked = this.isCutterMapperAdmin;
        let filtered = cutters.filter(c => c.cutterChamfer === chamfer);

        if (!unlocked) {
          const allowedNames = new Set<string>();
          filtered.forEach(c => {
            const famId = this.getCutterFamilyId(c);
            if ((c.cutterPhase_Name !== 'D' && c.cutterPhase_Name !== 'OBS' && c.name) ||
              (this.overrideUnlockedFamilyId != null && famId === this.overrideUnlockedFamilyId)) {
              allowedNames.add(c.name);
            }
          });
          filtered = filtered.filter(c => allowedNames.has(c.name));
        }

        const byName = new Map<string, string>();
        filtered.forEach(c => {
          if (c.name) {
            const group = c.cutterShapeDisplayName || c.pdcType || '';
            if (!byName.has(c.name)) {
              byName.set(c.name, group);
            }
          }
        });

        const flattened = Array
          .from(byName.entries())
          .map(([Name, group]) => ({
            Name,
            cutterShapeDisplayName: group,
            display: group ? `${group} - ${Name}` : Name
          }))
          .sort((a, b) => {
            const g = a.cutterShapeDisplayName.localeCompare(b.cutterShapeDisplayName);
            return g !== 0 ? g : a.Name.localeCompare(b.Name);
          });

        return groupBy(flattened, [{ field: 'cutterShapeDisplayName' }]) as GroupResult[];
      })
    );
  }
  private rebuildFromMappings(mappings: PatchORDCutterMapDto[]) {
    // 1) clear old controls
    Object.keys(this.appcutterMappingData.controls)
      .filter(k => /^\d+$/.test(k))
      .forEach(k => this.appcutterMappingData.removeControl(k));

    // 2) add one control per mapping
    mappings.forEach(m => {
      this.appcutterMappingData.addControl(
        m.id!.toString(),
        new FormControl(m.pdcCutter_Id ?? null)
      );
    });

    // 3) rebuild blades using the shared arm-ordering helpers
    const onlyNumbers = mappings
      .map(m => m.bladeNumber)
      .filter((n): n is number => n != null);
    const hasArms = mappings.some(m => m.armNumber != null);
    if (hasArms) {
      const { armForBlade, armsOrdered } = this.buildArmLookups(mappings);
      const bladesByArm = new Map<number, number[]>();
      armsOrdered.forEach(arm => bladesByArm.set(arm, []));
      Array.from(new Set(onlyNumbers)).forEach(blade => {
        const arm = armForBlade.get(blade);
        if (arm != null) bladesByArm.get(arm)?.push(blade);
      });
      this.blades = armsOrdered.flatMap((arm, armIndex) =>
        this.sortBladesWithinArm(bladesByArm.get(arm) ?? [], armIndex === 0)
      );
    } else {
      this.blades = Array.from(new Set(onlyNumbers)).sort((a, b) => a - b);
    }
  }
  private getMappingDiffs(): PatchORDCutterMapDto[] {
    // build a lookup of initialMappings by ID
    const initMap = new Map<number, PatchORDCutterMapDto>();
    for (const m of this._initialMappings) {
      if (m.id != null) {
        initMap.set(m.id, m);
      }
    }

    // compare against current mapping$
    return this.mapping$
      .getValue()
      .filter(m => {
        // only consider those with a real ID
        if (m.id == null) {
          return false;
        }
        const orig = initMap.get(m.id);
        // include if the cutter ID has changed
        return orig?.pdcCutter_Id !== m.pdcCutter_Id;
      })
      .map(m =>
        new PatchORDCutterMapDto({
          id: m.id,
          orderId: m.orderId,
          pdcCutter_Id: m.pdcCutter_Id!,
          bladeNumber: m.bladeNumber,
          createdDate: m.createdDate,
          facePosition: m.facePosition ?? '',
          modifiedBy: m.modifiedBy,
          modifiedDate: m.modifiedDate,
          numberOnRow: m.numberOnRow,
          rowNumber: m.rowNumber,
          shape_Id: m.shape_Id,
          shape_Type: m.shape_Type ?? '',
          zone: m.zone ?? ''
        })
      );
  }
  getCutterLineItemBackgroundColor(row: number){
    return this.colorCodes[row-1].Color ;
  }
  ngOnInit(): void {
    this.mapping$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mappings => this.rebuildFromMappings(mappings));

    this.cutterGroups$ = this.store.select(OptionState.cutterGroups);
    this.cutterGroups$
      .pipe(takeUntil(this.destroy$))
      .subscribe(groups => this.cutterGroupsList = groups ?? []);

    this.mapping$
      .pipe(
        skip(1),      
        takeUntil(this.destroy$)
      )
      .subscribe(maps => {
        const updates = maps
          .map(m => new PatchORDCutterMapDto({
            id: m.id,
            orderId: this.draftId,
            pdcCutter_Id: m.pdcCutter_Id!,
            bladeNumber: m.bladeNumber,
            createdDate: m.createdDate,
            facePosition: m.facePosition,
            modifiedBy: m.modifiedBy,
            modifiedDate: m.modifiedDate,
            numberOnRow: m.numberOnRow,
            rowNumber: m.rowNumber,
            shape_Id: m.shape_Id,
            shape_Type: m.shape_Type,
            zone: m.zone
          }));

        if (updates.length) {
          this.store.dispatch(new RequestActions.PatchRequestDraft({
            draftId: this.draftId!,
            changes: { cutterMaps: this.getMappingDiffs() }
          }));
        }
      });
    this.cutterQuantities$ = combineLatest([
      this.mapping$,
      this.rtaAvailableCutters$
    ]).pipe(
      filter(([maps, avail]) => maps.length > 0 && avail.length > 0),
      map(([maps, avail]) => {
        // Use a Map to preserve insertion (first-seen) order instead of
        // a plain object whose numeric keys get sorted ascending by JS,
        // which caused index gaps and color mismatches.
        const qtyMap = new Map<number, number>();
        maps.forEach(m => {
          if (m.pdcCutter_Id != null) {
            qtyMap.set(m.pdcCutter_Id, (qtyMap.get(m.pdcCutter_Id) || 0) + 1);
          }
        });

        let idx = 0;
        const result: { cutter: AvailableCutter; qty: number; index: number }[] = [];
        qtyMap.forEach((qty, id) => {
          const cutter = avail.find(c => c.pdcCutterID === id);
          if (cutter) {
            idx++;
            result.push({ cutter, qty, index: idx });
          }
        });
        return result;
      }),
      takeUntil(this.destroy$)
    );

    const pairs: [string, string][] = [
      ['PrimaryCutterChamfer', 'PrimaryCutterType'],
      ['SecondaryCutterChamfer', 'SecondaryCutterType'],
      ['PlaceCutterChamfer', 'PlaceCutterType'],
      ['ElementChamfer', 'ElementFamilyType'],
      ['ElementType', 'ElementChamfer'],
      ['ZoneChamfer', 'ZoneFamilyType']
    ];

    pairs.forEach(([srcName, tgtName]) => {
      const src = this.mappingTools.get(srcName)!;
      const tgt = this.mappingTools.get(tgtName)!;

      src.valueChanges
        .pipe(
          startWith(src.value),
          takeUntil(this.destroy$)
        )
        .subscribe(val => {
          val ? tgt.enable({ emitEvent: false }) : tgt.disable({ emitEvent: false });
        });
    });

    this.cutterQuantities$
      .pipe(takeUntil(this.destroy$))
      .subscribe(list => this.cutterQuantitiesList = list);
    const primaryChamferCtrl = this.mappingTools.get('PrimaryCutterChamfer') as FormControl;
    const secondaryChamferCtrl = this.mappingTools.get('SecondaryCutterChamfer') as FormControl;
    const placeChamferCtrl = this.mappingTools.get('PlaceCutterChamfer') as FormControl;
    const zoneChamferCtrl = this.mappingTools.get('ZoneChamfer') as FormControl;

    this.filteredPrimaryTypes$ = this.createFilteredTypes$(primaryChamferCtrl);
    this.filteredSecondaryTypes$ = this.createFilteredTypes$(secondaryChamferCtrl);
    this.filteredPlaceCutterTypes$ = this.createFilteredTypes$(placeChamferCtrl);
    this.filteredZoneTypes$ = this.createFilteredTypes$(zoneChamferCtrl);
    this.backupFamilies$ = combineLatest([
      this.rtaAvailableCutters$,
      this.cutterGroups$
    ]).pipe(
      map(([cutters, groups]) => {
        // Only include cutters whose group shape_Type is P or A
        const allowedGroupIds = new Set<number>(
          groups
            .filter(g => g.shape_Type === 'P' || g.shape_Type === 'A')
            .map(g => g.id)
        );
        const names = cutters
          .filter(c => {
            const grp = groups.find(g => g.name === c.pdcType);
            return grp && allowedGroupIds.has(grp.id);
          })
          .reduce((acc, c) => {
            if (c.name) {
              const groupName = c.cutterShapeDisplayName || c.pdcType || '';
              if (!acc.has(c.name)) {
                acc.set(c.name, groupName);
              }
            }
            return acc;
          }, new Map<string, string>());

        return Array.from(names.entries())
          .sort((a, b) => {
            const [nameA, groupA] = a;
            const [nameB, groupB] = b;
            const g = groupA.localeCompare(groupB);
            return g !== 0 ? g : nameA.localeCompare(nameB);
          })
          .map(([Name, group]) => ({ Name, group, display: group ? `${group} - ${Name}` : Name }));
      })
    );

    const groupCtrl = this.mappingTools.get('ElementType') as FormControl;
    const chamferCtrl = this.mappingTools.get('ElementChamfer') as FormControl;

    const selectedGroup$ = groupCtrl.valueChanges.pipe(startWith(groupCtrl.value));
    const selectedChamfer$ = chamferCtrl.valueChanges.pipe(startWith(chamferCtrl.value));

    this.filteredElementTypes$ = combineLatest([
      this.rtaAvailableCutters$,
      selectedGroup$,
      selectedChamfer$
    ]).pipe(
      map(([cutters, group, chamfer]) => {
        let filtered = cutters;
        if (chamfer) {
          filtered = filtered.filter(c => c.cutterChamfer === chamfer);
        }
        if (group) {
          filtered = filtered.filter(c => c.pdcType === group);
        }

        const unlocked = this.isCutterMapperAdmin;
        if (!unlocked) {
          const allowedNames = new Set<string>();
          filtered.forEach(c => {
            const famId = this.getCutterFamilyId(c);
            if ((c.cutterPhase_Name !== 'D' && c.cutterPhase_Name !== 'OBS' && c.name) ||
              (this.overrideUnlockedFamilyId != null && famId === this.overrideUnlockedFamilyId)) {
              allowedNames.add(c.name);
            }
          });
          filtered = filtered.filter(c => allowedNames.has(c.name));
        }

        const byName = new Map<string, string>();
        filtered.forEach(c => {
          if (c.name) {
            const groupName = c.cutterShapeDisplayName || c.pdcType || '';
            if (!byName.has(c.name)) {
              byName.set(c.name, groupName);
            }
          }
        });

        const flattened = Array.from(byName.entries())
          .map(([Name, groupName]) => ({
            Name,
            cutterShapeDisplayName: groupName,
            display: groupName ? `${groupName} - ${Name}` : Name
          }))
          .sort((a, b) => {
            const g = a.cutterShapeDisplayName.localeCompare(b.cutterShapeDisplayName);
            return g !== 0 ? g : a.Name.localeCompare(b.Name);
          });

        return groupBy(flattened, [{ field: 'cutterShapeDisplayName' }]) as GroupResult[];
      })
    );
  }
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cutterMappingInitial'] && this.cutterMappingInitial) {
      // seed both BehaviorSubject + form
      this.mapping$.next(this.cutterMappingInitial);
    }
    
    // Handle readonly state changes
    if (changes['readonly']) {
      if (this.readonly) {
        this.appcutterMappingData?.disable();
        this.mappingTools?.disable();
      } else {
        this.appcutterMappingData?.enable();
        this.mappingTools?.enable();
      }
    }
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getUniqueRows(blade: number): number[] {
    const rows = this.mapping$.getValue()
      .filter(m => m.bladeNumber === blade)
      .map(m => m.rowNumber);
    return Array.from(new Set(rows.filter((r): r is number => r != null))).sort((a, b) => a - b);
  }

  getFilteredRows(blade: number, row: number) {
    const mappings = this.mapping$.getValue();
    return mappings
      .filter(m => m.bladeNumber === blade && m.rowNumber === row)
      .sort((a, b) => (a.numberOnRow ?? 0) - (b.numberOnRow ?? 0));
  }

  getTableColor = (context: RowClassArgs) => {
    if (context?.dataItem) {
      return { [`${context.dataItem.ColorCode}`]: true };
    }
    else return { group: true }
  }
  getColor(groupNumber: number): string {
    const idx = groupNumber - 1;
    return this.colorCodes[idx]?.Color ?? '#9e9e9e';
  }
  public getColorByPdcCutterId(id?: number): string {
    if (id == null) {
      return '#9e9e9e';
    }
    const entry = this.cutterQuantitiesList.find(x => x.cutter.pdcCutterID === id);
    return entry ? this.getColor(entry.index) : '#9e9e9e';
  }
  ChangeTypePrimary() {
    const chamfer = this.mappingTools.get('PrimaryCutterChamfer')!.value as string;
    const selectedType = this.mappingTools.get('PrimaryCutterType')!.value as string;
    this.updateFullRow('Primary', chamfer, selectedType);
  }
  private updateFullRow(row: string, chamfer: string, selectedType: string) {
    // Determine which message properties to use based on row type
    const isPrimary = row === 'Primary';
    if (isPrimary) {
      this.primaryMessage = null;
      this.primaryDetails = null;
    } else {
      this.secondaryMessage = null;
      this.secondaryDetails = null;
    }

    if (!chamfer || !selectedType) {
      const msg = 'Please select both a chamfer and family type.';
      if (isPrimary) { this.primaryMessage = msg; } else { this.secondaryMessage = msg; }
      return;
    }

    const allowAll = this.isCutterMapperAdmin || this.overrideUnlocked$$.value || this.retrofit$$.value;
    const source$ = allowAll ? this.rtaAvailableCuttersStatuses$ : this.rtaAvailableCutters$;

    source$.pipe(take(1)).subscribe((cutters: AvailableCutter[]) => {
      // Build a lookup map for quick access by pdcCutterID
      const cuttersById = new Map<number, AvailableCutter>();
      cutters.forEach(c => cuttersById.set(c.pdcCutterID, c));

      let applied = 0;
      let skippedMissing = 0;
      let skippedNoMatch = 0;
      let total = 0;

      const mappings = this.mapping$.getValue().map(m => {
        // Skip PAD zone cutters when not in EJDF mode
        if (!this.isEJDF && this.isPadZoneCutter(m)) {
          return m;
        }
        
        // Determine if this mapping should be updated based on row type
        const isPrimaryRow = m.rowNumber === 1;
        const shouldUpdate = (row === 'Primary' && isPrimaryRow) || (row !== 'Primary' && !isPrimaryRow);
        
        if (!shouldUpdate) {
          return m;
        }

        total++;

        // Get the pdcShapeName for this mapping
        const pdcShapeName = this.getShapeNameForMapping(m, cuttersById);
        
        if (!pdcShapeName) {
          skippedMissing++;
          return m; // Can't match without shape name
        }

        // Find matching cutter with same chamfer, family, and pdcShapeName
        const match = this.findMatchingCutter(cutters, chamfer, selectedType, pdcShapeName);
        
        if (match) {
          applied++;
          return new PatchORDCutterMapDto({ ...m, pdcCutter_Id: match.pdcCutterID });
        }
        
        skippedNoMatch++;
        return m;
      });

      // Build feedback messages
      let message: string;
      let details: string | null = null;

      if (applied === 0) {
        message = 'No elements were updated with the new cutter mapping.';
        const detailParts: string[] = [];
        if (skippedMissing > 0) {
          detailParts.push(`${skippedMissing} element(s) skipped due to missing shape information.`);
        }
        if (skippedNoMatch > 0) {
          detailParts.push(`${skippedNoMatch} element(s) had no matching cutter for the selected chamfer/family/shape.`);
        }
        details = detailParts.length ? detailParts.join(' ') : null;
      } else {
        message = `${applied} of ${total} element(s) updated.`;
        const detailParts: string[] = [];
        if (skippedMissing > 0) {
          detailParts.push(`${skippedMissing} skipped (missing shape).`);
        }
        if (skippedNoMatch > 0) {
          detailParts.push(`${skippedNoMatch} skipped (no match).`);
        }
        details = detailParts.length ? detailParts.join(' ') : null;
      }

      if (isPrimary) {
        this.primaryMessage = message;
        this.primaryDetails = details;
      } else {
        this.secondaryMessage = message;
        this.secondaryDetails = details;
      }

      this.mapping$.next(mappings);
      this.rebuildBlades();
    });
  }
  ChangeChamferSecondary(chamfer: string) { /* … */ }
  ChangeTypeSecondary() {
    const chamfer = this.mappingTools.get('SecondaryCutterChamfer')!.value as string;
    const selectedType = this.mappingTools.get('SecondaryCutterType')!.value as string;
    this.updateFullRow('Secondary', chamfer, selectedType);
  }
  ChangeElementType(type: any) {
    const groupName = this.mappingTools.get('ElementType')!.value;
    this.elementType = groupName
    this.cutterGroups$
      .pipe(take(1))
      .subscribe(groups => {
        // find the matching group
        const match = groups.find(g => g.name === groupName);
        if (!match) {
          this.highlightedElements = [];    // no match → no highlights
          return;
        }

        // build your new highlighted‐IDs array in one go
        this.highlightedElements = this.mapping$
          .getValue()
          .filter(m =>
            m.shape_Type === match.shape_Type &&
            m.shape_Id === match.id
          )
          .map(m => m.id ?? 0);
        console.log(this.highlightedElements)
      });

  }
  private applySelectionToElements(options: {
    chamfer?: string | null;
    familyType?: string | null;
    groupName?: string | null;
    elementIds: number[];
    clearSelection?: boolean;
    messageTarget?: 'selection' | 'elementGroup';
  }): void {
    const target = options.messageTarget ?? 'selection';
    const setMessage = (msg: string | null) => {
      if (target === 'elementGroup') { this.elementGroupMessage = msg; } else { this.selectionMessage = msg; }
    };
    const setDetails = (det: string | null) => {
      if (target === 'elementGroup') { this.elementGroupDetails = det; } else { this.selectionDetails = det; }
    };

    setMessage(null);
    setDetails(null);
    const { chamfer, familyType, elementIds, clearSelection = true } = options;
    
    if (!chamfer || !familyType || !elementIds?.length) {
      return;
    }

    const familyKey = this.normalizeKey((familyType as any)?.Name ?? familyType);

    this.rtaAvailableCutters$.pipe(take(1)).subscribe((cutters) => {
      // Build a lookup map for quick access by pdcCutterID
      const cuttersById = new Map<number, AvailableCutter>();
      cutters.forEach(c => cuttersById.set(c.pdcCutterID, c));

      let applied = false;
      let skippedMissing = 0;
      let skippedNoMatch = 0;
      const availableSizes = new Set<string>();

      // Collect available sizes for this chamfer/family combination for error messaging
      cutters.forEach(c => {
        if (this.normalizeKey(c.cutterChamfer) === this.normalizeKey(chamfer) &&
            this.normalizeKey(c.name) === familyKey) {
          if (c.cutterSize) {
            availableSizes.add(c.cutterSize);
          }
        }
      });

      const updated = this.mapping$
        .getValue()
        .map(m => {
          if (!elementIds.includes(m.id!)) {
            return m;
          }

          // Get the pdcShapeName for this mapping
          const pdcShapeName = this.getShapeNameForMapping(m, cuttersById);

          if (!pdcShapeName) {
            skippedMissing++;
            return m;
          }

          // Find matching cutter with same chamfer, family, and pdcShapeName
          const match = this.findMatchingCutter(cutters, chamfer, familyKey, pdcShapeName);

          if (match?.pdcCutterID != null) {
            applied = true;
            return new PatchORDCutterMapDto({ ...m, pdcCutter_Id: match.pdcCutterID });
          }

          skippedNoMatch++;
          return m;
        });

      if (!applied) {
        const distinctSizes = Array.from(availableSizes.values());
        const shapeNames = this.mapping$
          .getValue()
          .filter(m => elementIds.includes(m.id!))
          .map(m => this.getShapeNameForMapping(m, cuttersById))
          .filter((name): name is string => !!name && name !== 'undefined');
        const distinctShapes = Array.from(new Set(shapeNames));
        setMessage('No elements were updated with the new cutter mapping.');
        const detailParts: string[] = [];
        if (skippedMissing > 0) {
          detailParts.push(`${skippedMissing} element(s) skipped due to missing shape information.`);
        }
        if (skippedNoMatch > 0) {
          detailParts.push(`${skippedNoMatch} element(s) had no matching cutter for the selected chamfer/family/shape.`);
        }
        if (distinctSizes.length) {
          detailParts.push(`Available sizes for this family/chamfer: ${distinctSizes.join(', ')}`);
        }
        if (distinctShapes.length) {
          detailParts.push(`Affected shapes: ${distinctShapes.join(', ')}`);
        }
        setDetails(detailParts.length ? detailParts.join(' ') : null);
        return;
      }

      this.mapping$.next(updated);

      this.rebuildBlades();
      if (clearSelection) {
        this.ClearSelectedElements();
      }

      setMessage('Cutter selection applied.');
      setDetails(null);
    });
  }

  applyPlaceSelection() {
    const chamfer = this.mappingTools.get('PlaceCutterChamfer')?.value as string;
    const familyType = this.mappingTools.get('PlaceCutterType')?.value as string;
    console.log(familyType, chamfer)
    this.applySelectionToElements({
      chamfer,
      familyType,
      elementIds: [...this.selectedElements]
    });
  }

  ApplyGlobal() {
    const groupName = this.mappingTools.get('ElementType')!.value;
    const chamfer = this.mappingTools.get('ElementChamfer')!.value;
    const familyType = this.mappingTools.get('ElementFamilyType')!.value;

    this.applySelectionToElements({
      chamfer,
      familyType,
      groupName,
      elementIds: [...this.selectedElements],
      messageTarget: 'elementGroup'
    });
  }

  ApplyGlobalBackups() {
    this.selectionMessage = null;
    this.selectionDetails = null;

    const familyRaw = this.mappingTools.get('BackupFamilyType')?.value;
    const familyKey = this.normalizeKey((familyRaw as any)?.Name ?? familyRaw);
    const elementIds = [...this.selectedElements];

    if (!familyKey) {
      this.selectionMessage = 'Select a backup family to apply.';
      return;
    }

    if (!elementIds.length) {
      this.selectionMessage = 'Select one or more elements to apply the backup cutter.';
      return;
    }

    combineLatest([
      this.rtaAvailableCutters$.pipe(take(1)),
      this.cutterGroups$.pipe(take(1))
    ]).subscribe(([cutters, groups]) => {
      // Build a lookup map for quick access by pdcCutterID
      const cuttersById = new Map<number, AvailableCutter>();
      cutters.forEach(c => cuttersById.set(c.pdcCutterID, c));

      // Filter cutters to only those with matching family and allowed group types (P or A)
      const allowedGroups = new Map<number, string>();
      groups
        .filter(g => g.shape_Type === 'P' || g.shape_Type === 'A')
        .forEach(g => allowedGroups.set(g.id, g.shape_Type));

      const backupCutters = cutters.filter(c => {
        const grp = groups.find(g => g.name === c.pdcType);
        return grp && allowedGroups.has(grp.id) && this.normalizeKey(c.name) === familyKey;
      });

      if (!backupCutters.length) {
        this.selectionMessage = 'No matching backup cutters found for the selected family.';
        return;
      }

      let applied = false;
      let skippedDueToPrimaryMatch = 0;
      let skippedNoMatch = 0;

      const updated = this.mapping$
        .getValue()
        .map(m => {
          if (!elementIds.includes(m.id!)) {
            return m;
          }

          // Get the pdcShapeName for this mapping
          const pdcShapeName = this.getShapeNameForMapping(m, cuttersById);

          if (!pdcShapeName) {
            skippedNoMatch++;
            return m;
          }

          // Find matching backup cutter with same family and pdcShapeName
          // Note: For backups we match by family and shape, not chamfer
          const match = backupCutters.find(c =>
            this.normalizeKey(c.pdcShapeName) === this.normalizeKey(pdcShapeName)
          );

          if (match) {
            // Validate that backup differs from primary
            if (match.pdcCutterID === m.pdcCutter_Id) {
              skippedDueToPrimaryMatch++;
              return m; // Skip - backup would be same as primary
            }
            applied = true;
            return new PatchORDCutterMapDto({ ...m, pdcCutter_Id: match.pdcCutterID });
          }

          skippedNoMatch++;
          return m;
        });

      if (!applied) {
        const shapeNames = this.mapping$
          .getValue()
          .filter(m => elementIds.includes(m.id!))
          .map(m => this.getShapeNameForMapping(m, cuttersById))
          .filter((name): name is string => !!name && name !== 'undefined');
        const distinctShapes = Array.from(new Set(shapeNames));
        
        if (skippedDueToPrimaryMatch > 0) {
          this.selectionMessage = 'Backup cutter cannot be the same as the primary cutter.';
          this.selectionDetails = `${skippedDueToPrimaryMatch} element(s) skipped because the selected backup matches the primary.`;
        } else {
          this.selectionMessage = 'No backup cutters matched the selected elements.';
          this.selectionDetails = distinctShapes.length ? `Affected shapes: ${distinctShapes.join(', ')}` : null;
        }
        return;
      }

      // Show warning if some were skipped
      if (skippedDueToPrimaryMatch > 0) {
        this.selectionDetails = `${skippedDueToPrimaryMatch} element(s) skipped because backup matched primary.`;
      }

      this.mapping$.next(updated);
      this.rebuildBlades();
      this.ClearSelectedElements();
      this.selectionMessage = 'Backup cutters applied.';
    });
  }

  ClearCutterSelections() {
    const cleared = this.mapping$.getValue().map(m => new PatchORDCutterMapDto({
      ...m,
      pdcCutter_Id: undefined
    }));
    this.mapping$.next(cleared);

    Object.keys(this.appcutterMappingData.controls).forEach(controlName => {
      const ctl = this.appcutterMappingData.get(controlName);
      if (ctl) {
        ctl.setValue(null);
      }
    });
  }
  ClearSelectedElements() {
    this.selectedElements = [];
    this.highlightedElements = [];
  }
  ChangeChamferByGroup(chamfer: string, group: any) { /* … */ }
  ChangeTypeByGroup(type: string, group: any) { /* … */ }

  ChangeChamferByZone(chamfer: string, zone: any) { /* … */ }
  ChangeTypeByZone(type: string, zone: any) { /* … */ }

  changeRowChamfer(chamfer: string, blade: any, row: any) { /* … */ }
  changeRowType(type: string, blade: any, row: any) { }

  changeCutterChamfer(chamfer: string, cutter: any) { }
  changeCutterType(cutterId: number, cutter: any) { }

  applyZonePlacement() {
    this.zoneMessage = null;
    this.zoneDetails = null;
    this.overrideError = null;

    const zoneValue = this.mappingTools.get('ZoneName')?.value as any;
    const zone = typeof zoneValue === 'string' ? zoneValue : zoneValue?.name;
    const locationValue = this.mappingTools.get('ZoneLocation')?.value as any;
    const location = typeof locationValue === 'string' ? locationValue : locationValue?.name;
    const chamfer = this.mappingTools.get('ZoneChamfer')?.value as string;
    const selectedType = this.mappingTools.get('ZoneFamilyType')?.value as string;
    
    if (!zone || !location || !chamfer || !selectedType) {
      this.zoneMessage = 'Zone, location, chamfer, and family are required to apply.';
      return;
    }

    // Prevent applying to PAD zone when not in EJDF mode
    if (!this.isEJDF && this.normalizeKey(zone) === 'pad') {
      this.zoneMessage = 'PAD zone cutters can only be modified in EJDF mode.';
      return;
    }

    const isPrimary = location.toString().toLowerCase() === 'primary';
    const zoneKey = this.normalizeKey(zone);
    const allowAll = this.isCutterMapperAdmin || this.overrideUnlocked$$.value || this.retrofit$$.value;
    const source$ = allowAll ? this.rtaAvailableCuttersStatuses$ : this.rtaAvailableCutters$;

    source$.pipe(take(1)).subscribe((cutters: AvailableCutter[]) => {
      // Build a lookup map for quick access by pdcCutterID
      const cuttersById = new Map<number, AvailableCutter>();
      cutters.forEach(c => cuttersById.set(c.pdcCutterID, c));

      let applied = 0;
      let skippedMissing = 0;
      let skippedNoMatch = 0;
      let total = 0;

      const updated = this.mapping$
        .getValue()
        .map(m => {
          // Check if this mapping is in the target zone
          const isZoneMatch = this.normalizeKey(m.zone) === zoneKey;
          if (!isZoneMatch) {
            return m;
          }

          // Check if this is the target row (primary or secondary)
          const isTargetRow = isPrimary ? m.rowNumber === 1 : (m.rowNumber ?? 0) > 1;
          if (!isTargetRow) {
            return m;
          }

          total++;

          // Get the pdcShapeName for this mapping
          const pdcShapeName = this.getShapeNameForMapping(m, cuttersById);
          
          if (!pdcShapeName) {
            skippedMissing++;
            return m;
          }

          // Find matching cutter with same chamfer, family, and pdcShapeName
          const match = this.findMatchingCutter(cutters, chamfer, selectedType, pdcShapeName);

          if (match) {
            applied++;
            return new PatchORDCutterMapDto({ ...m, pdcCutter_Id: match.pdcCutterID });
          }

          skippedNoMatch++;
          return m;
        });

      if (applied === 0) {
        this.zoneMessage = 'No elements were updated for the selected zone.';
        const detailParts: string[] = [];
        if (skippedMissing > 0) {
          detailParts.push(`${skippedMissing} element(s) skipped due to missing shape information.`);
        }
        if (skippedNoMatch > 0) {
          detailParts.push(`${skippedNoMatch} element(s) had no matching cutter for the selected chamfer/family/shape.`);
        }
        this.zoneDetails = detailParts.length ? detailParts.join(' ') : null;
        return;
      }

      this.zoneMessage = `${applied} of ${total} element(s) updated in zone ${zone}.`;
      const detailParts: string[] = [];
      if (skippedMissing > 0) {
        detailParts.push(`${skippedMissing} skipped (missing shape).`);
      }
      if (skippedNoMatch > 0) {
        detailParts.push(`${skippedNoMatch} skipped (no match).`);
      }
      this.zoneDetails = detailParts.length ? detailParts.join(' ') : null;

      this.mapping$.next(updated);
      this.rebuildBlades();
    });
  }

  initializeSelects() { /* … */ }
  ToggleSelect(cutter: any) {
    if (!this.allowSelect)
      return;
    
    // Prevent selecting PAD zone cutters when not in EJDF mode
    if (!this.isEJDF && this.isPadZoneCutter(cutter)) {
      return;
    }
    
    if (this.isCutterSelected(cutter))
      this.selectedElements = this.selectedElements.filter(n => n !== cutter.id);
    else
      this.selectedElements.push(cutter.id);
  }
  
  /**
   * Checks if a cutter is in the PAD zone
   */
  isPadZoneCutter(cutter: any): boolean {
    const zone = (cutter?.zone || '').toString().toUpperCase().trim();
    return zone === 'PAD';
  }
  
  isCutterHighlighted(cutter: any): boolean {
    return this.highlightedElements.includes(cutter.id);
  }
  isCutterSelected(cutter: any): boolean {
    return this.selectedElements.includes(cutter.id);
  }
  ToggleAllowSelect() {
    this.allowSelect = !this.allowSelect;
    if (!this.allowSelect)
      this.ClearSelectedElements();
  }
  showOptions(): boolean {
    return (this.isEJDF)
  }

  /**
   * Sets the active blade/row context for multi-cutter operations
   */
  setActiveRow(bladeNo: number, rowNo: number): void {
    this.activeBladeNo = bladeNo;
    this.activeRowNo = rowNo;
  }

  /**
   * Adds a single cutter to the end of the specified row
   */
  addCutterToRow(bladeNo: number, rowNo: number): void {
    if (this.readonly) return;
    
    const mappings = this.mapping$.getValue();
    const rowCutters = mappings.filter(m => m.bladeNumber === bladeNo && m.rowNumber === rowNo);
    
    // Find the max numberOnRow for this row
    const maxNumberOnRow = rowCutters.length > 0 
      ? Math.max(...rowCutters.map(c => c.numberOnRow ?? 0)) 
      : 0;
    
    // Generate a temporary negative ID for new cutters (will be replaced by backend)
    const tempId = -Date.now();
    
    // Create a new cutter entry
    const newCutter = new PatchORDCutterMapDto({
      id: tempId,
      orderId: this.draftId,
      bladeNumber: bladeNo,
      rowNumber: rowNo,
      numberOnRow: maxNumberOnRow + 1,
      facePosition: '',
      pdcCutter_Id: undefined,
      zone: rowCutters[0]?.zone ?? '',
      shape_Id: rowCutters[0]?.shape_Id,
      shape_Type: rowCutters[0]?.shape_Type
    });
    
    this.mapping$.next([...mappings, newCutter]);
  }

  /**
   * Removes the last cutter from the specified row
   */
  removeCutterFromRow(bladeNo: number, rowNo: number): void {
    if (this.readonly) return;
    
    const mappings = this.mapping$.getValue();
    const rowCutters = mappings.filter(m => m.bladeNumber === bladeNo && m.rowNumber === rowNo);
    
    if (rowCutters.length === 0) return;
    
    // Find the cutter with the highest numberOnRow
    const lastCutter = rowCutters.reduce((max, c) => 
      (c.numberOnRow ?? 0) > (max.numberOnRow ?? 0) ? c : max
    );
    
    // Remove it from mappings
    const updated = mappings.filter(m => m.id !== lastCutter.id);
    this.mapping$.next(updated);
  }

  /**
   * Appends multiple cutters to the active row based on multiCutterAdd value
   */
  appendCutters(): void {
    if (this.readonly) return;
    
    const count = Math.max(1, Math.floor(this.multiCutterAdd || 1));
    
    for (let i = 0; i < count; i++) {
      this.addCutterToRow(this.activeBladeNo, this.activeRowNo);
    }
  }

  /**
   * Deletes an entire row from a blade
   */
  deleteRow(bladeNo: number, rowNo: number): void {
    if (this.readonly) return;
    
    const mappings = this.mapping$.getValue();
    const updated = mappings.filter(m => !(m.bladeNumber === bladeNo && m.rowNumber === rowNo));
    
    this.mapping$.next(updated);
    this.rebuildBlades();
  }

  /**
   * Copies all cutter assignments from one row to another
   */
  copyRowToRow(sourceBladeNo: number, sourceRowNo: number, targetBladeNo: number, targetRowNo: number): void {
    if (this.readonly) return;
    
    const mappings = this.mapping$.getValue();
    const sourceCutters = mappings.filter(m => m.bladeNumber === sourceBladeNo && m.rowNumber === sourceRowNo);
    const targetCutters = mappings.filter(m => m.bladeNumber === targetBladeNo && m.rowNumber === targetRowNo);
    
    if (sourceCutters.length === 0 || targetCutters.length === 0) return;
    
    // Copy pdcCutter_Id from source to target cutters by position (numberOnRow)
    const updated = mappings.map(m => {
      if (m.bladeNumber === targetBladeNo && m.rowNumber === targetRowNo) {
        const sourceCutter = sourceCutters.find(s => s.numberOnRow === m.numberOnRow);
        if (sourceCutter && sourceCutter.pdcCutter_Id) {
          return new PatchORDCutterMapDto({
            ...m,
            pdcCutter_Id: sourceCutter.pdcCutter_Id
          });
        }
      }
      return m;
    });
    
    this.mapping$.next(updated);
  }

  lastRowonBlade(blade: number, row: number){
    if(this.getUniqueRows(blade).length == (row+1))
    {return true;}
    else{return false;}
  }

  private normalizeKey(val: any): string {
    if (val === null || val === undefined) {
      return '';
    }
    if (typeof val === 'string') {
      return val.toLowerCase().trim();
    }
    if (typeof val === 'object') {
      const candidate = (val as any).Name ?? (val as any).name ?? (val as any).value ?? (val as any).text;
      if (typeof candidate === 'string') {
        return candidate.toLowerCase().trim();
      }
    }
    return val.toString ? val.toString().toLowerCase().trim() : '';
  }

  /**
   * Finds a matching cutter from available cutters based on chamfer, family, and pdcShapeName.
   * @param cutters - List of available cutters to search
   * @param chamfer - The selected chamfer value
   * @param familyName - The selected family/type name
   * @param pdcShapeName - The pdcShapeName from the current cutter mapping
   * @returns The matching AvailableCutter or undefined if not found
   */
  private findMatchingCutter(
    cutters: AvailableCutter[],
    chamfer: string,
    familyName: string,
    pdcShapeName: string
  ): AvailableCutter | undefined {
    const chamferKey = this.normalizeKey(chamfer);
    const familyKey = this.normalizeKey(familyName);
    const shapeKey = this.normalizeKey(pdcShapeName);

    return cutters.find(c =>
      this.normalizeKey(c.cutterChamfer) === chamferKey &&
      this.normalizeKey(c.name) === familyKey &&
      this.normalizeKey(c.pdcShapeName) === shapeKey
    );
  }

  /**
   * Gets the pdcShapeName for a cutter mapping.
   * Uses the stored pdcShapeName if available, otherwise looks up from current cutter assignment.
   */
  private getShapeNameForMapping(
    mapping: PatchORDCutterMapDto,
    cuttersById: Map<number, AvailableCutter>
  ): string {
    // First try to use pdcShapeName directly from the mapping
    if (mapping.pdcShapeName) {
      return mapping.pdcShapeName;
    }
    // Fallback: look up from current cutter assignment
    if (mapping.pdcCutter_Id != null) {
      const currentCutter = cuttersById.get(mapping.pdcCutter_Id);
      if (currentCutter?.pdcShapeName) {
        return currentCutter.pdcShapeName;
      }
    }
    // Last resort: use shape name from cutter groups
    return this.getShapeNameById(mapping.shape_Id) ?? '';
  }

  private trimmedMinLengthValidator(min: number): ValidatorFn {
    return (control: AbstractControl) => {
      const raw = control.value;
      const val = (raw === null || raw === undefined) ? '' : raw.toString().trim();
      if (!val) {
        return { required: true };
      }
      return val.length >= min ? null : { minlength: { requiredLength: min, actualLength: val.length } };
    };
  }

  private getCutterFamilyId(c: AvailableCutter): number | null {
    // Try several possible property names for family/type ID coming from API
    const anyC = c as any;
    return anyC?.dgnFCCutterTypesID
      ?? anyC?.DgnFCCutterTypesID
      ?? anyC?.cutterFamily_Id
      ?? anyC?.cutterFamilyId
      ?? anyC?.cutterFamilyID
      ?? anyC?.pdcFamilyTypeId
      ?? anyC?.pdcFamilyTypeID
      ?? anyC?.familyId
      ?? anyC?.familyID
      ?? anyC?.id
      ?? null;
  }

  submitOverrideRequest() {
    this.overrideMessage = null;
    this.overrideError = null;

    if (!this.draftId) {
      this.overrideError = 'No order id available.';
      return;
    }
    if (this.overrideForm.invalid) {
      this.overrideForm.markAllAsTouched();
      return;
    }

    const cutterFamilyId = this.overrideForm.value.cutterFamily as number;
    const cutterFamily = this.overrideFamiliesList.find(f => f.id === cutterFamilyId);
    if (!cutterFamily) {
      this.overrideError = 'Select a cutter family.';
      return;
    }
    const justification = (this.overrideForm.value.justification ?? '').toString().trim();
    const requesterEmail = this.overrideForm.value.requesterEmail;

    const payload: CreateCutterOverrideRequestDto = {
      cutterFamilyId: cutterFamily.id,
      cutterFamilyName: cutterFamily.name,
      justification,
      requesterEmail
    };

    this.overrideSubmitting = true;
    this.rtaApi.createOverrideRequest(this.draftId, this.rtaApi.$CURRENT_USER_ID, payload)
      .pipe(
        finalize(() => this.overrideSubmitting = false)
      )
      .subscribe({
        next: res => {
          const msg = typeof res?.status === 'string' ? res.status : 'Override request submitted.';
          this.overrideMessage = msg;
          this.overrideForm.reset();
        },
        error: err => {
          this.overrideError = err?.message ?? 'Failed to submit override request.';
        }
      });
  }

  applyOverrideCode() {
    this.overrideMessage = null;
    this.overrideError = null;

    if (!this.draftId) {
      this.overrideError = 'No order id available.';
      return;
    }

    if (this.overrideCodeForm.invalid) {
      this.overrideCodeForm.markAllAsTouched();
      this.overrideError = 'Enter a code to validate.';
      return;
    }
    const code = (this.overrideCodeForm.get('overrideCode')?.value || '').toString().trim();

    this.overrideSubmitting = true;
    this.rtaApi.validateOverrideCode({ orderId: this.draftId, code })
      .pipe(finalize(() => this.overrideSubmitting = false))
      .subscribe({
        next: res => {
          if (res?.status) {
            const info = res.info as any;
            this.overrideUnlockedFamilyId = info?.cutterFamily_Id ?? info?.CutterFamily_Id;
            this.overrideUnlocked$$.next(true);
            this.overrideMessage = 'Override code accepted. Restricted families unlocked for this session.';
            this.overrideCodeForm.reset();
          } else {
            this.overrideError = res?.message ?? 'Invalid code.';
          }
        },
        error: err => {
          this.overrideError = err?.message ?? 'Failed to validate code.';
        }
      });
  }

  public getShapeNameById(shapeId?: number): string | undefined {
    if (shapeId == null) {
      return 'undefined';
    }
    return this.cutterGroupsList.find(g => g.id === shapeId)?.name;
  }
  public downloadMappedFile(): void {
    window.open(`http://hdbstechdev/ibscp/download?rta=${this.draftId}`, '_blank');
  }
}

